import { useCallback, useMemo } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import styled from "styled-components";
import Meta from "../Meta";
import { InstrumentTypeResponse } from "../codegen-api";
import NotificationEffects from "../components/NotificationEffects";
import Footer from "../components/Footer";
import MobileTabs from "../components/MobileTabs";
import Announcement from "../components/shared/Announcement";
import { Header } from "../components/shared/Header";
import LiquidationRiskBanner from "../components/shared/LiquidationRiskBanner";
import { MobileHeader } from "../components/shared/MobileHeader";
import {
  getDefaultInstrumentType,
  getTradableTradingTypeBySegment,
  getTradingTypeBySegment,
} from "../config/features";
import { MarketInstrumentContextProvider } from "../contexts/MarketInstrumentContext";
import { OrderContextProvider } from "../contexts/OrderContext";
import { PageEndpointEnum } from "../enums/endpoint";
import useScreenSize from "../hooks/screenSize/useScreenSize";
import { AssetResponse } from "../utils/asset";
import { getDefaultTradingAsset } from "../utils/defaultMarket";
import {
  TradingRoutePathParamsEnum,
  getTradingRoutePath,
} from "./tradingRoute";

const PageScrollWrapper = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
`;

export function OutletRoute() {
  // UPDATE CONTEXT WHEN ROUTES CHANGES
  const p = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobileScreen } = useScreenSize();

  const currentPage: PageEndpointEnum = useMemo(() => {
    const { pathname } = location;
    const allPages = Object.keys(PageEndpointEnum);
    const matchedRouteKeys: string[] = allPages.filter((k) => {
      const route = PageEndpointEnum[k as keyof typeof PageEndpointEnum];
      return pathname.includes(route);
    });

    // Get the last matched route
    if (matchedRouteKeys.length) {
      const lastMatchedKey = matchedRouteKeys[matchedRouteKeys.length - 1];
      return PageEndpointEnum[lastMatchedKey as keyof typeof PageEndpointEnum];
    }
    return PageEndpointEnum.TRADING;
  }, [location]);

  // This fixes params keep rerendering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => p, [JSON.stringify(p)]);

  // Derive the asset, derivative, and instrument from the params.
  // eg. if the URL is /option/eth/ETH-07JUL23-1850-C,
  // returns asset = ETH, derivative = option, instrument = ETH-07JUL23-1850-C
  const assetDerivativeParams = useMemo(() => {
    if (currentPage === PageEndpointEnum.TRADING) {
      // The vertical is now a STATIC route segment (`/perpetual/:asset/...`),
      // not a `:derivative` param, so read it off the pathname's first segment.
      // `/` (root trading) has no segment → undefined → default vertical.
      const d = location.pathname.split("/").filter(Boolean)[0];
      // A segment only counts when a trading vertical claims it AND that
      // vertical is enabled for this build. An unknown OR disabled segment
      // resolves to `undefined`, which `onAssetDerivativeChanged` below
      // normalises to the default vertical — so /spot/btc with spot switched
      // off redirects instead of rendering a dead page.
      const tradingType = getTradableTradingTypeBySegment(d);
      const derivative = tradingType?.instrumentType ?? undefined;
      const a = params[TradingRoutePathParamsEnum.ASSET];
      const rawAsset = a?.toUpperCase() || "";
      // Asset remapping keys off what the URL SAYS, not off the gated
      // derivative: `/spot/btc` on a build without spot must still be read as a
      // spot link, so BTC is not rewritten to WBTC before the redirect picks
      // the fallback asset. Otherwise a shared spot link lands on the wrong
      // market entirely.
      const segmentType = getTradingTypeBySegment(d);
      const isPerp = segmentType?.instrumentType === InstrumentTypeResponse.Perpetual;
      const isSpot = segmentType?.instrumentType === InstrumentTypeResponse.Spot;
      const isMonad = process.env.REACT_APP_CHAIN_ID === "10143";
      // Spot markets trade the real base asset (e.g. BTC-USDC), so DON'T remap
      // BTC→WBTC for spot — otherwise the spot market can't be matched and the
      // route falls back to the default perp. The wrap mapping is for derivatives.
      const displayToInternal: Record<string, string> = isPerp
        ? { GOLD: "XAU", SILVER: "XAG" }
        : isSpot
          ? {}
          : {
              ...(isMonad ? {} : { BTC: "WBTC" }),
            };
      const validAsset = displayToInternal[rawAsset] || rawAsset;

      return {
        asset: validAsset ? (validAsset as AssetResponse) : undefined,
        derivative,
        instrument: params[TradingRoutePathParamsEnum.INSTRUMENT_NAME],
      };
    }
    return undefined;
  }, [currentPage, params, location.pathname]);

  const onAssetDerivativeChanged = useCallback(
    (
      asset?: AssetResponse,
      derivative?: InstrumentTypeResponse,
      instrumentName?: string
    ) => {
      // Ignore if is not trading path
      if (currentPage !== PageEndpointEnum.TRADING) {
        return;
      }

      const resolvedDerivative = derivative || getDefaultInstrumentType();
      const newPath = getTradingRoutePath(
        resolvedDerivative,
        asset || getDefaultTradingAsset(),
        resolvedDerivative === InstrumentTypeResponse.Option
          ? instrumentName
          : undefined
      );
      // Preserve the query string. This callback only NORMALISES the path when
      // the market/derivative changes, so dropping search would silently discard
      // deep-linkable state that lives there — the bottom-bar panel
      // (`?panel=trade-history`), `?agent=1`, and any future URL-backed tab.
      // `navigate(path)` replaces the whole location, so search must be explicit.
      navigate(
        { pathname: newPath, search: location.search },
        { replace: true }
      );
    },
    [currentPage, navigate, location.search]
  );
  return (
    <MarketInstrumentContextProvider
      params={assetDerivativeParams}
      onAssetDerivativeChanged={onAssetDerivativeChanged}
    >
      <OrderContextProvider>
      <NotificationEffects />
      {!isMobileScreen ? (
        <Announcement key="alert" announcementType="alert" />
      ) : null}
      {!isMobileScreen ? (
        <Announcement key="feature" announcementType="feature" />
      ) : null}
      {!isMobileScreen ? <Header /> : <MobileHeader />}
      {/* Global, all-pages liquidation-risk bar (>=90% to liquidation) */}
      <LiquidationRiskBanner />
      <Meta />
      <PageScrollWrapper>
        <Outlet />
      </PageScrollWrapper>
      {!isMobileScreen ? (
        <Footer />
      ) : (
        <MobileTabs navigate={navigate} currentPage={currentPage} />
      )}
      </OrderContextProvider>
    </MarketInstrumentContextProvider>
  );
}
